/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  /**
   * Whether a video is left for the shopper to start by hand.
   *
   * Product media videos in the gallery are given a play button on mobile, so
   * nothing may autoplay them: the tap on that button is what starts them, and
   * it is also the gesture the browser needs before it will allow their sound.
   * Desktop is unaffected and keeps autoplaying.
   * @param {Element} el - Element inside the product media to test.
   * @returns {boolean}
   */
  function isTapToPlay(el) {
    const media = el.closest('.product-media--video');
    if (!media || !media.querySelector('.js-play-video')) return false;
    return window.matchMedia('(max-width: 768px)').matches;
  }

  /**
   * The product media of a video the shopper has to start by hand, if it is one.
   *
   * With 'Autoplay videos' off, product media videos keep their poster and play
   * button on every screen size, and the click on that button is the only thing
   * that may start them.
   * @param {Element} el - Element inside the product media to test.
   * @returns {?Element} The product media, or null if its videos may autoplay.
   */
  function clickToPlayMedia(el) {
    const media = el.closest('.product-media--video');
    return media && media.dataset.autoplay === 'false' ? media : null;
  }

  /**
   * Whether a video is still waiting behind its poster for that play button.
   *
   * A press on it records itself as the media's load trigger, so anything else
   * that would start the video - the intersection observer, a slide change -
   * can tell that the shopper has not asked for playback yet.
   * @param {Element} el - Element inside the product media to test.
   * @returns {boolean}
   */
  function isClickToPlay(el) {
    const media = clickToPlayMedia(el);
    return !!media && media.loadTrigger !== 'click';
  }

  /**
   * Hands a video to Video.js.
   *
   * Video.js only claims the videos it can find while the page is still
   * loading, and a gallery further down the page can be unpacked after that,
   * leaving its video with no control bar and with none of the options in its
   * data-setup applied, looping included. Adopting it here covers that.
   * @param {HTMLVideoElement} video - Video element to hand over.
   * @returns {?object} The Video.js player, or null if there is none.
   */
  function adoptVideoJs(video) {
    if (!window.videojs || !video.hasAttribute('data-setup')) return null;
    if (video.player) return video.player;

    try {
      return window.videojs(video);
    } catch (err) {
      console.warn('Unable to initialise Video.js player', err);  // eslint-disable-line
      return null;
    }
  }

  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
    }

    connectedCallback() {
      this.init();
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        this.closest('.video-section').addEventListener('click', this.play.bind(this));
      }
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');

      // A video the shopper has to start by hand is unpacked ahead of the press
      // (see deferred-media), but possibly after Video.js's own sweep has given
      // up, so hand it over here.
      //
      // Never inside the press itself, though: Video.js lays down the source it
      // read off the tag a moment after taking the element over, and that
      // cancels the play the press has just made - which showed as a video that
      // answered its play button with a control bar and nothing else. Unpacked
      // that late, the element is better off plain: it keeps the loop attribute
      // from the markup, and a play made in the press is left to stand.
      const unpackedByPress = this.closest('deferred-media')
        && this.closest('deferred-media').loadTrigger === 'click';

      if (clickToPlayMedia(this) && !unpackedByPress) {
        this.vjsPlayer = adoptVideoJs(this.player);

        // Video.js may hand back a copy of the tag rather than the tag itself -
        // it does on iOS, where moving a media element in the DOM costs it its
        // state - and it only builds that copy once its own setup reaches the
        // tech, which can be after this. So follow the swap when it happens,
        // or everything below would be watching an element that has since been
        // taken off the page.
        if (this.vjsPlayer && typeof this.vjsPlayer.ready === 'function') {
          this.vjsPlayer.ready(() => this.adoptSwappedElement());
        }
      }

      this.setupClickToPlay();

      // Video.js's autoSetup adopts this element moments from now, wrapping it
      // in a player and keeping its control bar. Both players then hold the same
      // <video>, which is fine: they talk to it through its own API and its
      // play/pause events, and Video.js is told not to claim taps on the video
      // itself (see the userActions option in media.liquid), so on mobile the
      // shopper's tap is still this theme's to handle - and its sound with it.

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        this.closest('.video-section').classList.add('video-section--played');
      } else {
        this.player.addEventListener('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.addObserver(this.player);

        // Mute the video if a click didn't trigger its load
        const deferredMedia = this.closest('deferred-media');
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          // Autoplay is only ever granted to a muted element.
          this.player.muted = true;
        } else if (!this.background && this.closest('.product-media--video')) {
          // The click on the play button is itself the gesture that authorises
          // audio, so this video can start with sound.
          this.player.muted = false;
        }

        this.play();
      }
    }

    /**
     * Picks the video element back up after Video.js has swapped it out.
     *
     * Only iOS gets here: everywhere else Video.js keeps the tag it was given.
     */
    adoptSwappedElement() {
      const swapped = this.vjsPlayer.el().querySelector('video');
      if (!swapped || swapped === this.player) return;

      this.player = swapped;
      swapped.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });
      this.addObserver(swapped);
    }

    /**
     * Wires up a video the shopper starts by hand.
     *
     * With 'Autoplay videos' off the poster stays over the video, with its play
     * button on it, until the shopper presses play - and comes back when a
     * video that isn't looping reaches its end, so it can be watched again.
     *
     * The press plays the element directly, there and then. Sound is the reason
     * it has to be that immediate: iOS grants it only to a play() made inside
     * the gesture that asked for it, never to one handed off to an event
     * handler afterwards. This is the same way custom.js starts the autoplaying
     * videos on mobile, which is the path already known to keep its sound.
     */
    setupClickToPlay() {
      const media = clickToPlayMedia(this);
      if (!media) return;

      // Bound on the media rather than on the element, and in the capture phase
      // since play and ended don't bubble: which element is the live one is
      // Video.js's business, and on iOS it is not the one that started here.
      media.addEventListener('play', () => media.classList.add('is-video-started'), true);

      media.addEventListener('ended', (evt) => {
        if (evt.target.loop) return;
        media.classList.remove('is-video-started');
      }, true);

      const playBtn = media.querySelector('.js-load-media');
      if (!playBtn) return;

      // deferred-media has its own listener on this button, but its content is
      // already unpacked by now and it bows out, leaving this free to play.
      playBtn.addEventListener('click', (evt) => {
        evt.preventDefault();

        const video = media.querySelector('video');
        if (!video) return;

        this.pausedByUser = false;
        if (video.ended) video.currentTime = 0;
        video.muted = false;
        media.classList.add('is-video-started');

        // The press is what deferred-media would have recorded had it been the
        // thing to unpack the content, and recording it is what lets the
        // viewport observer pick the video back up after a scroll away.
        media.loadTrigger = 'click';

        const playing = video.play();
        if (playing && typeof playing.catch === 'function') {
          // Sound refused: a muted element is the one thing always allowed to
          // play, and a silent video beats a still frame.
          playing.catch(() => {
            video.muted = true;
            const retry = video.play();
            if (retry && typeof retry.catch === 'function') retry.catch(() => {});
          });
        }
      });
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.closest('.video-section').classList.add('video-section--played');
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser || isTapToPlay(this) || isClickToPlay(this)) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
      } else {
        this.player.play();
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        }, { rootMargin: '0px 0px 200px 0px' });
      });

      observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}
